import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * useTopicUsage — Stage 5 cascade-aware UX support
 *
 * Returns a `Map<topic_id, excerpt_count>` for the given workspace so the
 * Topic Tree editor can show usage counts and warn the user before mutating
 * topics/columns that would unlink existing excerpts.
 *
 * Topics-on-excerpts is currently the only assignment surface (see
 * `mem://features/topics-are-codex-driven.md`). When other surfaces are
 * added, extend this hook to sum those too.
 */
export function useTopicUsage(workspaceId: string | null | undefined) {
  const [usage, setUsage] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!workspaceId) {
      setUsage(new Map());
      return;
    }
    setLoading(true);
    try {
      const { data: excerpts } = await supabase
        .from("excerpts")
        .select("id")
        .eq("workspace_id", workspaceId);
      const excerptIds = (excerpts ?? []).map((e: { id: string }) => e.id);
      const map = new Map<string, number>();
      if (excerptIds.length > 0) {
        const { data: links } = await supabase
          .from("excerpt_topics")
          .select("topic_id")
          .in("excerpt_id", excerptIds);
        (links ?? []).forEach((row: { topic_id: string }) => {
          map.set(row.topic_id, (map.get(row.topic_id) ?? 0) + 1);
        });
      }
      // Corpus document assignments also count toward Topic usage so the
      // Codex editor warns before unlinking docs.
      const { data: corpusLinks } = await (supabase as unknown as {
        from: (t: string) => {
          select: (c: string) => {
            eq: (k: string, v: string) => Promise<{ data: Array<{ framework_topic_id: string }> | null }>;
          };
        };
      })
        .from("workspace_corpus_document_topics")
        .select("framework_topic_id")
        .eq("workspace_id", workspaceId);
      (corpusLinks ?? []).forEach((row: { framework_topic_id: string }) => {
        map.set(row.framework_topic_id, (map.get(row.framework_topic_id) ?? 0) + 1);
      });
      setUsage(map);
    } finally {
      setLoading(false);
    }

  }, [workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { usage, loading, refresh };
}
